import React, { useEffect, useMemo, useRef, useState } from 'react';
import PatientPage from 'pages/rpm/patients/patient';
import useUser from 'utils/useUser';
import ModuleProvider from 'infrastructure/providers/module-provider';
import { useNavigate, useParams } from 'react-router-dom';
import { isValidGuid } from 'infrastructure/functions';
import {
  patientPageAccessCcmStatuses,
  patientPageAccessRpmStatuses,
} from 'infrastructure/consts';
import {
  showResult,
  useAsyncEffect,
  useShowConfirmBeforeCloseTab,
  useConfirmExit,
} from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import { Routes } from 'router/routes';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';
import BaseLoader from 'components/atoms/base-loader';
import ChartSessionTimer from 'components/atoms/chart-session-timer';
import { usePatientChartSession } from 'infrastructure/hooks/patient/use-patient-chart-session';
import PatientActivityContext from 'infrastructure/providers/patient-activity-context';
import { useMultiPatientCall } from 'infrastructure/providers/multi-patient-call-context';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import UnsavedChangesProvider, {
  useUnsavedChanges,
} from 'infrastructure/providers/unsaved-changes/unsaved-changes';
import BlockedSessionModal from 'components/atoms/blocked-session-modal';
import BlockedSessionContext from 'infrastructure/providers/blocked-session-context';

import UnifiedPatientProfile from './index';

const Page: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { Alert, showAlert } = useAlert();

  const { hasUnsavedChanges, showUnsavedModal } = useUnsavedChanges();

  const isIdleTimeRedirect = useRef(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedCountdown, setBlockedCountdown] = useState(0);
  const { isPatient, hasClinicalRole, isPractice } = useUser();
  const { patientGuid } = useParams();

  const {
    query: { patient, isLoading },
  } = useRpmPatientDetails({ patientGuid });

  const canRunSession = !isPatient && !isPractice && hasClinicalRole;

  const {
    sessionId,
    serverElapsedTime,
    rpmTime,
    ccmTime,
    idleState,
    onActivity,
    blocked,
    activePatientGuid,
    endSession,
  } = usePatientChartSession({
    patientGuid: patientGuid as string,
    canRun: canRunSession,
    onTimeout: () => {
      if (hasClinicalRole) {
        isIdleTimeRedirect.current = true;
        navigate(Routes.overviewDashboard, { replace: true });
      }
    },
  });

  useEffect(() => {
    if (!blocked) return;
    setShowBlockedModal(true);
    setBlockedCountdown(30);
    const interval = setInterval(() => {
      setBlockedCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          window.close();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [blocked]);

  useEffect(() => {
    if (!canRunSession || blocked) return;
    const handler = () => onActivity();
    document.addEventListener('input', handler, true);
    document.addEventListener('change', handler, true);
    return () => {
      document.removeEventListener('input', handler, true);
      document.removeEventListener('change', handler, true);
    };
  }, [canRunSession, blocked]);

  const { onPatientVisited } = useMultiPatientCall();

  const handleCloseSession = async () => {
    navigate(Routes.overviewDashboard);
  };

  useEffect(() => {
    if (isPatient || isPractice) return;
    if (patientGuid) onPatientVisited(patientGuid);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && patientGuid)
        onPatientVisited(patientGuid);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [patientGuid, onPatientVisited, isPatient, isPractice]);

  useAsyncEffect(async () => {
    if (hasClinicalRole && !isPractice && idleState === 'warning') {
      const result = await showAlert({
        title: '',
        messageTitle: t('messages.patientChartIdleWarning'),
        showReject: false,
        closable: false,
        successBtnLabel: t('labels.confirm'),
      });
      if (onActivity && result) {
        onActivity();
      }
    }
  }, [hasClinicalRole, idleState, isPractice]);

  const hasAccess = useMemo(() => {
    if (!patient) return false;
    const hasRpmAccess = patientPageAccessRpmStatuses.includes(
      patient.profile.status,
    );
    const hasCcmAccess = patientPageAccessCcmStatuses.includes(
      patient.profile.ccmStatus,
    );
    return hasRpmAccess || hasCcmAccess;
  }, [patient]);

  useEffect(() => {
    if (!patientGuid || !isValidGuid(patientGuid)) {
      showResult(t('errors.invalidGuid'));
      navigate(Routes.notFound, { replace: true });
    }
  }, [patientGuid, navigate]);

  useEffect(() => {
    if (patient && !hasAccess) {
      navigate(Routes.notFound, { replace: true });
    }
  }, [patient, hasAccess]);

  useConfirmExit(async () => {
    let res: boolean = true;
    if (isIdleTimeRedirect.current) return res;
    if (hasUnsavedChanges) {
      res = await showUnsavedModal();
      if (res) {
        endSession(sessionId);
      }
      return res;
    }
    res = await showAlert({
      title: '',
      successBtnLabel: t('labels.confirm'),
      messageTitle: t('messages.closeChartSession'),
      closable: false,
    });
    if (res) {
      endSession(sessionId);
    }
    return res;
  }, !blocked && (canRunSession || hasUnsavedChanges));

  useShowConfirmBeforeCloseTab(!blocked && canRunSession);

  return (
    <BaseLoader loading={isLoading || !hasAccess}>
      <PatientActivityContext.Provider value={{ onActivity }}>
        <BlockedSessionContext.Provider value={blocked}>
          <ModuleProvider>
            {isPatient ? (
              <PatientPage />
            ) : (
              <UnifiedPatientProfile sessionId={sessionId} />
            )}
          </ModuleProvider>
        </BlockedSessionContext.Provider>
      </PatientActivityContext.Provider>
      {canRunSession && patientGuid && (
        <ChartSessionTimer
          sessionId={sessionId}
          serverElapsedTime={serverElapsedTime}
          rpmTime={rpmTime}
          ccmTime={ccmTime}
          onClose={handleCloseSession}
        />
      )}
      {blocked && activePatientGuid && showBlockedModal && (
        <BlockedSessionModal
          activePatientGuid={activePatientGuid}
          countdown={blockedCountdown}
          onDismiss={() => setShowBlockedModal(false)}
        />
      )}
      {Alert}
    </BaseLoader>
  );
};

const PatientProfile: React.FC = () => {
  return (
    <UnsavedChangesProvider>
      <Page />
    </UnsavedChangesProvider>
  );
};

export default PatientProfile;
